import {
  INITIAL_WORKFLOW_STATE,
  type CallWorkflowState,
  type WorkflowAction,
  type WorkflowReducerState,
} from "@/lib/modules/calls/workflow/types";
import {
  findNextNonSkippedQueueTask,
  findQueueTaskIndex,
  reconcileQueuePosition,
} from "@/lib/modules/calls/workflow/utils";

/** Restore states where the UI shows an in-progress call/log flow for the task. */
const RESTORE_STATES_IMPLYING_ACTIVE_TASK: CallWorkflowState[] = [
  "CONNECTING",
  "ACTIVE_CALL",
  "POST_CALL_LOGGING",
];

const VALID_STATE_TRANSITIONS: Record<CallWorkflowState, CallWorkflowState[]> =
  {
    IDLE: ["CONNECTING", "COUNTDOWN", "PAUSED"],
    CONNECTING: ["ACTIVE_CALL", "IDLE", "COUNTDOWN", "POST_CALL_LOGGING"],
    ACTIVE_CALL: ["POST_CALL_LOGGING", "IDLE", "COUNTDOWN"],
    POST_CALL_LOGGING: ["CONNECTING", "COUNTDOWN", "IDLE"],
    COUNTDOWN: ["CONNECTING", "IDLE", "PAUSED", "POST_CALL_LOGGING"],
    PAUSED: ["IDLE", "CONNECTING", "COUNTDOWN"],
  };

/** Pure workflow state machine reducer */
export function workflowReducer(
  state: WorkflowReducerState,
  action: WorkflowAction,
): WorkflowReducerState {
  switch (action.type) {
    case "START_WORKFLOW": {
      const skippedSet = new Set<number>();
      for (const task of action.payload.queue) {
        if (task.skippedAt !== null) {
          skippedSet.add(task.id);
        }
      }
      for (const task of action.payload.scheduledStack) {
        if (task.skippedAt !== null) {
          skippedSet.add(task.id);
        }
      }

      const firstTask = findNextNonSkippedQueueTask(
        action.payload.queue,
        skippedSet,
        null,
        null,
      );
      const queuePosition = findQueueTaskIndex(
        action.payload.queue,
        firstTask?.id ?? null,
      );

      const nextState = {
        ...state,
        state: "COUNTDOWN" as const,
        sessionId: action.payload.sessionId,
        sessionMarbleAdminId: action.payload.marbleAdminId,
        stateBeforePause: null,
        awaitingFirstCall: false,
        queue: action.payload.queue,
        scheduledStack: action.payload.scheduledStack,
        queuePosition,
        currentTaskId: firstTask?.id ?? null,
        completedCount: action.payload.completedCount,
        skippedSet,
        completedTaskIds: new Set<number>(),
        sessionCompletedTaskIds: new Set<number>(),
        isLoading: false,
        error: null,
        isPanelVisible: true,
        sessionStartedAt: action.payload.sessionStartedAt,
        convertedCount: 0,
        sessionBaselineCompletedCount: action.payload.completedCount,
        sessionBaselineSkippedCount: skippedSet.size,
        sessionSkippedCount: 0,
        callLogSaved: false,
        pendingTaskNavigation: null,
        scheduledPromptTask: null,
        lastCallStartedAt: action.payload.sessionStartedAt.toISOString(),
        sessionVersion: action.payload.version ?? 0,
      };

      return nextState;
    }

    case "RESTORE_WORKFLOW": {
      const skippedSet = new Set(action.payload.skippedTaskIds);
      for (const task of action.payload.queue) {
        if (task.skippedAt !== null) {
          skippedSet.add(task.id);
        }
      }
      for (const task of action.payload.scheduledStack) {
        if (task.skippedAt !== null) {
          skippedSet.add(task.id);
        }
      }

      let restoredState = action.payload.state;
      let currentTaskId = action.payload.currentTaskId;
      let awaitingFirstCall =
        action.payload.awaitingFirstCall ??
        (action.payload.state === "PAUSED" &&
          action.payload.currentTaskId === null);

      // Legacy sessions started paused until resume. Land on prep instead.
      if (awaitingFirstCall && restoredState === "PAUSED") {
        restoredState = "COUNTDOWN";
        awaitingFirstCall = false;
        if (currentTaskId == null) {
          currentTaskId =
            findNextNonSkippedQueueTask(
              action.payload.queue,
              skippedSet,
              null,
              null,
            )?.id ?? null;
        }
      }

      // Do not infer "this task is done — skip to the next family" just
      // because the fresh queue dropped `currentTaskId`. Completing/logging a
      // call removes it from the queue before the admin clicks "Go to next
      // task"; treating that as an advance is why refreshing the current
      // student jumps the autodialer forward. Callers resolve the restored
      // id first (see resolveRestoredWorkflowCurrentTaskId).
      const isCurrentTaskScheduled = action.payload.scheduledStack.some(
        (task) => task.id === currentTaskId,
      );
      const isCurrentTaskInQueue =
        findQueueTaskIndex(action.payload.queue, currentTaskId) !== null;

      const queuePosition = isCurrentTaskScheduled
        ? action.payload.queuePosition
        : reconcileQueuePosition(
            action.payload.queue,
            currentTaskId,
            action.payload.queuePosition,
          );

      // Keep restored calls non-dialable until popup STATE either confirms the
      // live phase or the admin finishes the orphaned call log.
      const wasOrphanedCall =
        restoredState === "CONNECTING" || restoredState === "ACTIVE_CALL";
      if (wasOrphanedCall) {
        restoredState = "POST_CALL_LOGGING";
      } else if (
        currentTaskId == null &&
        !isCurrentTaskScheduled &&
        RESTORE_STATES_IMPLYING_ACTIVE_TASK.includes(restoredState)
      ) {
        restoredState = "PAUSED";
      }

      // A logged call is already gone from the queue. Treat the log as saved
      // so "Go to next task" stays available instead of re-opening a blank
      // form (and risking a duplicate log).
      const callLogSaved =
        currentTaskId != null &&
        !isCurrentTaskInQueue &&
        !isCurrentTaskScheduled;

      return {
        ...state,
        state: restoredState,
        sessionId: action.payload.sessionId,
        sessionMarbleAdminId: action.payload.marbleAdminId,
        stateBeforePause: null,
        awaitingFirstCall,
        queue: action.payload.queue,
        scheduledStack: action.payload.scheduledStack,
        queuePosition,
        currentTaskId,
        completedCount: action.payload.completedCount,
        skippedSet,
        completedTaskIds: new Set<number>(),
        sessionCompletedTaskIds: new Set<number>(),
        isLoading: false,
        error: null,
        sessionStartedAt: action.payload.sessionStartedAt,
        convertedCount: action.payload.convertedCount,
        sessionBaselineCompletedCount:
          action.payload.sessionBaselineCompletedCount,
        sessionBaselineSkippedCount: action.payload.sessionBaselineSkippedCount,
        sessionSkippedCount: action.payload.sessionSkippedCount,
        callLogSaved,
        pendingTaskNavigation: null,
        scheduledPromptTask: null,
        isPanelVisible: action.payload.isPanelVisible ?? state.isPanelVisible,
        lastCallStartedAt:
          action.payload.lastCallStartedAt ??
          action.payload.sessionStartedAt.toISOString(),
        sessionVersion: action.payload.version ?? 0,
      };
    }

    case "STOP_WORKFLOW":
      return {
        ...INITIAL_WORKFLOW_STATE,
        completedCount: state.completedCount,
      };

    case "PAUSE_WORKFLOW":
      if (state.state === "PAUSED") {
        return state;
      }

      return {
        ...state,
        stateBeforePause: state.state,
        state: "PAUSED",
      };

    case "RESUME_WORKFLOW":
      if (state.state !== "PAUSED") {
        return state;
      }

      if (state.awaitingFirstCall) {
        const taskId = _resolveTaskIdForResume(state);

        return {
          ...state,
          state: "CONNECTING",
          stateBeforePause: null,
          awaitingFirstCall: false,
          currentTaskId: taskId,
        };
      }

      return {
        ...state,
        state: state.stateBeforePause ?? "IDLE",
        stateBeforePause: null,
      };

    case "TRANSITION_STATE": {
      const currentState = state.state;
      const newState = action.payload.state;

      if (!VALID_STATE_TRANSITIONS[currentState]?.includes(newState)) {
        return state;
      }

      const didFinishCallWork =
        RESTORE_STATES_IMPLYING_ACTIVE_TASK.includes(currentState) &&
        !RESTORE_STATES_IMPLYING_ACTIVE_TASK.includes(newState);
      const isSameTaskRetry =
        currentState === "POST_CALL_LOGGING" && newState === "CONNECTING";

      return {
        ...state,
        state: newState,
        callLogSaved: isSameTaskRetry ? false : state.callLogSaved,
        lastCallStartedAt:
          newState === "CONNECTING" || didFinishCallWork
            ? new Date().toISOString()
            : state.lastCallStartedAt,
      };
    }

    case "SET_QUEUE": {
      const pendingSkip = new Set(action.payload.pendingSkipTaskIds ?? []);
      const pendingUnskip = new Set(action.payload.pendingUnskipTaskIds ?? []);
      const skippedSet = new Set<number>();

      for (const task of action.payload.queue) {
        if (pendingUnskip.has(task.id)) {
          continue;
        }
        if (pendingSkip.has(task.id) || task.skippedAt !== null) {
          skippedSet.add(task.id);
        }
      }

      const scheduledStack =
        action.payload.scheduledStack ?? state.scheduledStack;
      for (const task of scheduledStack) {
        if (pendingUnskip.has(task.id)) {
          continue;
        }
        if (pendingSkip.has(task.id) || task.skippedAt !== null) {
          skippedSet.add(task.id);
        }
      }

      for (const taskId of state.skippedSet) {
        if (pendingUnskip.has(taskId)) {
          continue;
        }
        skippedSet.add(taskId);
      }

      const isCurrentTaskScheduled = state.scheduledStack.some(
        (task) => task.id === state.currentTaskId,
      );

      return {
        ...state,
        queue: action.payload.queue,
        scheduledStack: action.payload.scheduledStack ?? state.scheduledStack,
        skippedSet,
        // Server data already excludes completed tasks; clear the optimistic set.
        completedTaskIds: new Set<number>(),
        queuePosition: isCurrentTaskScheduled
          ? state.queuePosition
          : reconcileQueuePosition(
              action.payload.queue,
              state.currentTaskId,
              state.queuePosition,
            ),
      };
    }

    case "SET_QUEUE_POSITION":
      return {
        ...state,
        queuePosition: action.payload.position,
      };

    case "SET_CURRENT_TASK_ID":
      return {
        ...state,
        currentTaskId: action.payload.taskId,
        callLogSaved: false,
      };

    case "INCREMENT_COMPLETED_COUNT":
      return {
        ...state,
        completedCount: state.completedCount + 1,
      };

    case "DECREMENT_COMPLETED_COUNT":
      return {
        ...state,
        completedCount: Math.max(0, state.completedCount - 1),
      };

    case "SKIP_TASK": {
      const newSkipped = new Set(state.skippedSet);
      const isNew = !newSkipped.has(action.payload.taskId);
      newSkipped.add(action.payload.taskId);
      return {
        ...state,
        skippedSet: newSkipped,
        sessionSkippedCount: isNew
          ? state.sessionSkippedCount + 1
          : state.sessionSkippedCount,
      };
    }

    case "UNSKIP_TASK": {
      const unSkipped = new Set(state.skippedSet);
      const wasPresent = unSkipped.has(action.payload.taskId);
      unSkipped.delete(action.payload.taskId);
      return {
        ...state,
        skippedSet: unSkipped,
        sessionSkippedCount: wasPresent
          ? Math.max(0, state.sessionSkippedCount - 1)
          : state.sessionSkippedCount,
      };
    }

    case "SET_LOADING":
      return {
        ...state,
        isLoading: action.payload.loading,
      };

    case "SET_ERROR":
      return {
        ...state,
        error: action.payload.error,
      };

    case "SET_SESSION_VERSION":
      return {
        ...state,
        sessionVersion: action.payload.version,
      };

    case "SET_PANEL_VISIBILITY":
      return {
        ...state,
        isPanelVisible: action.payload.visible,
      };

    case "INCREMENT_CONVERTED":
      return {
        ...state,
        convertedCount: state.convertedCount + 1,
      };

    case "DECREMENT_CONVERTED":
      return {
        ...state,
        convertedCount: Math.max(0, state.convertedCount - 1),
      };

    case "SET_CALL_LOG_SAVED":
      return {
        ...state,
        callLogSaved: action.payload.saved,
      };

    case "SET_PENDING_TASK_NAVIGATION":
      return {
        ...state,
        pendingTaskNavigation: action.payload.navigation,
      };

    case "SET_SCHEDULED_PROMPT":
      return {
        ...state,
        scheduledPromptTask: action.payload.task,
      };

    case "REMOVE_FROM_SCHEDULED_STACK":
      return {
        ...state,
        scheduledStack: state.scheduledStack.filter(
          (t) => t.id !== action.payload.taskId,
        ),
      };

    case "RESTORE_TO_SCHEDULED_STACK": {
      const alreadyPresent = state.scheduledStack.some(
        (t) => t.id === action.payload.task.id,
      );
      if (alreadyPresent) {
        return state;
      }
      return {
        ...state,
        scheduledStack: [...state.scheduledStack, action.payload.task],
      };
    }

    case "MARK_TASK_COMPLETED": {
      const next = new Set(state.completedTaskIds);
      next.add(action.payload.taskId);
      const sessionNext = new Set(state.sessionCompletedTaskIds);
      sessionNext.add(action.payload.taskId);
      return {
        ...state,
        completedTaskIds: next,
        sessionCompletedTaskIds: sessionNext,
      };
    }

    case "UNMARK_TASK_COMPLETED": {
      const next = new Set(state.completedTaskIds);
      next.delete(action.payload.taskId);
      const sessionNext = new Set(state.sessionCompletedTaskIds);
      sessionNext.delete(action.payload.taskId);
      return {
        ...state,
        completedTaskIds: next,
        sessionCompletedTaskIds: sessionNext,
      };
    }

    case "RECLASSIFY_EXPIRED_SCHEDULED": {
      const isCurrentTaskScheduled = state.scheduledStack.some(
        (task) => task.id === state.currentTaskId,
      );
      const firstTask =
        state.currentTaskId == null
          ? findNextNonSkippedQueueTask(
              action.payload.queue,
              state.skippedSet,
              null,
              null,
              state.completedTaskIds,
            )
          : null;
      let queuePosition = reconcileQueuePosition(
        action.payload.queue,
        state.currentTaskId,
        state.queuePosition,
      );
      if (isCurrentTaskScheduled) {
        queuePosition = state.queuePosition;
      }
      if (firstTask) {
        queuePosition = findQueueTaskIndex(action.payload.queue, firstTask.id);
      }

      return {
        ...state,
        queue: action.payload.queue,
        scheduledStack: action.payload.scheduledStack,
        currentTaskId: firstTask?.id ?? state.currentTaskId,
        queuePosition,
        state:
          firstTask && (state.state === "PAUSED" || state.state === "IDLE")
            ? "COUNTDOWN"
            : state.state,
      };
    }

    case "APPLY_SYNCED_STATE": {
      const incoming = action.payload.state;
      return {
        ...incoming,
        skippedSet:
          incoming.skippedSet instanceof Set
            ? incoming.skippedSet
            : new Set(incoming.skippedSet),
        completedTaskIds:
          incoming.completedTaskIds instanceof Set
            ? incoming.completedTaskIds
            : new Set(incoming.completedTaskIds),
        sessionCompletedTaskIds:
          incoming.sessionCompletedTaskIds instanceof Set
            ? incoming.sessionCompletedTaskIds
            : new Set(incoming.sessionCompletedTaskIds),
        sessionStartedAt:
          incoming.sessionStartedAt instanceof Date ||
          incoming.sessionStartedAt == null
            ? incoming.sessionStartedAt
            : new Date(incoming.sessionStartedAt),
        isPanelVisible: state.isPanelVisible,
        pendingTaskNavigation: state.pendingTaskNavigation,
        isLoading: false,
        error: null,
      };
    }

    default:
      return state;
  }
}

const _resolveTaskIdForResume = (
  state: WorkflowReducerState,
): number | null => {
  if (state.currentTaskId != null) {
    return state.currentTaskId;
  }

  if (state.queuePosition !== null && state.queue[state.queuePosition]) {
    return state.queue[state.queuePosition].id;
  }

  const firstTask = findNextNonSkippedQueueTask(
    state.queue,
    state.skippedSet,
    null,
    null,
  );
  return firstTask?.id ?? null;
};
